import io from 'socket.io-client';
import {
  addNotification,
  markNotificationAsRead,
} from '../store/slices/notificationsSlice';
import {
  addMessage,
  updateConversation,
} from '../store/slices/messagesSlice';
import {
  addLoveshot,
  updateLoveshot,z
} from '../store/slices/loveshotsSlice';
import {
  addStory,
  updateStoryStats,
} from '../store/slices/storiesSlice';


let storeInstance = null;

export const setStore = (store) => {
  storeInstance = store;
};

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
  }

  connect(token) {
    if (this.socket && this.isConnected) {
      return;
    }

    const socketUrl = process.env.REACT_APP_SOCKET_URL;
    // Disable WebSocket if no socket server configured
    if (!socketUrl || socketUrl === 'disabled' || socketUrl === '') {
      console.info('Socket.IO désactivé (pas de serveur configuré)');
      return;
    }

    // Connect to Socket.IO server
    this.socket = io(socketUrl, {
      auth: {
        token: token,
      },
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => {
      console.log('Connected to socket server');
      this.isConnected = true;
    });

    this.socket.on('disconnect', () => {
      console.log('Disconnected from socket server');
      this.isConnected = false;
    });

    this.setupEventListeners();
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  setupEventListeners() {
    // Listen for loveshot creation
    this.socket.on('loveshots', (data) => {
      if (data.event === 'created') {
        storeInstance.dispatch(addLoveshot(data.data));
      } else if (data.event === 'updated') {
        storeInstance.dispatch(updateLoveshot(data.data));
      }
    });

    // Listen for messages
    this.socket.on('messages', (data) => {
      if (data.event === 'sent') {
        const message = data.data;
        const state = storeInstance.getState();
        const currentUserId = state.auth.user?.id;
        
        // Check if message already exists in state to avoid duplicates
        const existingMessage = state.messages.messages.find(m => m.id === message.id);
        if (!existingMessage) {
          storeInstance.dispatch(addMessage({ message, user: state.auth.user }));
          storeInstance.dispatch(updateConversation({
            id: message.conversation_id,
            lastMessage: message,
            updated_at: new Date().toISOString(),
          }));
        }
      }
    });



    // Listen for typing indicators
    this.socket.on('typing', (data) => {
      const { setTypingUser } = require('../store/slices/messagesSlice');
      storeInstance.dispatch(setTypingUser({
        conversationId: data.conversation_id,
        userId: data.user_id,
        isTyping: data.is_typing
      }));
    });

    // Listen for message reactions
    this.socket.on('reactions', (data) => {
      const { addReaction, removeReaction } = require('../store/slices/messagesSlice');
      if (data.event === 'added') {
        storeInstance.dispatch(addReaction({
          messageId: data.message_id,
          reaction: data.reaction
        }));
      } else if (data.event === 'removed') {
        storeInstance.dispatch(removeReaction({
          messageId: data.message_id,
          userId: data.user_id
        }));
      }
    });

    // Listen for message status updates
    this.socket.on('message-status', (data) => {
      const { updateMessageStatus } = require('../store/slices/messagesSlice');
      storeInstance.dispatch(updateMessageStatus(data));
    });

    // Listen for streak updates
    this.socket.on('streak', (data) => {
      const { updateStreak } = require('../store/slices/messagesSlice');
      storeInstance.dispatch(updateStreak({
        conversationId: data.conversation_id,
        days: data.days
      }));
    });


    // Listen for notifications
    this.socket.on('notifications', (data) => {
      if (data.event === 'sent') {
        storeInstance.dispatch(addNotification(data.data));
      }
    });

    // Listen for stories
    this.socket.on('stories', (data) => {
      if (data.event === 'created') {
        storeInstance.dispatch(addStory(data.data));
      } else if (data.event === 'updated') {
        storeInstance.dispatch(updateStoryStats(data.data));
      }

    });

    // Listen for user status updates
    this.socket.on('user-status', (data) => {
      // Handle user online/offline status updates
      console.log('User status update:', data);
    });
  }

  // Join a private channel (for user-specific events)
  joinUserChannel(userId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join-user-channel', userId);
    }
  }

  // Leave a private channel
  leaveUserChannel(userId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave-user-channel', userId);
    }
  }

  // Join a conversation channel
  joinConversation(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join-conversation', conversationId);
    }
  }

  // Leave a conversation channel
  leaveConversation(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave-conversation', conversationId);
    }
  }

  // Update user status
  updateUserStatus(status) {
    if (this.socket && this.isConnected) {
      this.socket.emit('update-status', status);
    }
  }

  // Send typing indicator
  sendTyping(conversationId, isTyping) {
    if (this.socket && this.isConnected) {
      this.socket.emit('typing', {
        conversation_id: conversationId,
        is_typing: isTyping
      });
    }
  }

  // Send message reaction
  sendReaction(messageId, reaction) {
    if (this.socket && this.isConnected) {
      this.socket.emit('reaction', {
        message_id: messageId,
        reaction: reaction
      });
    }
  }

  // Remove message reaction
  removeReaction(messageId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('remove-reaction', {
        message_id: messageId
      });
    }
  }

  // Mark messages as read
  markAsRead(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('mark-read', {
        conversation_id: conversationId
      });
    }
  }

  // Send message event to broadcast to conversation participants
  sendMessage(message) {
    if (this.socket && this.isConnected) {
      this.socket.emit('message-sent', {
        conversationId: message.conversation_id,
        message: message
      });
    }
  }
}



const socketService = new SocketService();
export default socketService;
